import { useEffect, useMemo, useRef } from "react";
import { faBell } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { PanelEmptyState } from "@/components/mission-control/PanelEmptyState";
import type { Alert } from "@/services/api/guardiansv2/types";
import { AlertCard } from "./AlertCard";
import type { ScopeLabelCache } from "../audits/useScopeLabelCache";

export interface AlertsPaneProps {
  alerts: Alert[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  onLoadMore: () => void;
  scopeLabels: ScopeLabelCache;
  selectedAlertId: string | null;
  onSelect: (alertId: string) => void;
  onArchive: (alertId: string) => void;
}

/**
 * Scrollable list of open Guardians alerts.
 */
export function AlertsPane({
  alerts,
  loading,
  loadingMore,
  error,
  hasMore,
  onLoadMore,
  scopeLabels,
  selectedAlertId,
  onSelect,
  onArchive,
}: AlertsPaneProps) {
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = sentinelRef.current?.parentElement;
    const target = sentinelRef.current;
    if (!root || !target || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) onLoadMore();
      },
      { root, rootMargin: "200px" }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [hasMore, onLoadMore]);

  const sorted = useMemo(
    () =>
      [...alerts].sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      ),
    [alerts]
  );

  if (loading && alerts.length === 0) {
    return (
      <div className="py-12 text-center text-sm text-slate-400" data-testid="alerts-loading">
        Loading alerts…
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-12 text-center text-sm text-red-300" data-testid="alerts-error">
        {error}
      </div>
    );
  }

  if (sorted.length === 0) {
    return (
      <PanelEmptyState
        icon={faBell}
        title="No open alerts"
        subtitle="Alert screening results for tasks and missions appear here when issues are detected."
        testId="alerts-empty"
      />
    );
  }

  return (
    <div className="space-y-2" data-testid="alerts-list">
      {sorted.map((alert) => (
        <AlertCard
          key={alert._id}
          alert={alert}
          scopeLabels={scopeLabels}
          selected={selectedAlertId === alert._id}
          onSelect={() => onSelect(alert._id)}
          onArchive={() => onArchive(alert._id)}
        />
      ))}
      {hasMore ? <div ref={sentinelRef} className="h-4" aria-hidden /> : null}
      {loadingMore ? (
        <p className="py-2 text-center text-xs text-slate-500">Loading more…</p>
      ) : null}
    </div>
  );
}
